// Bilingual content dictionary for the ZMEX Customer Portal landing page.
// Toggled via the header "Español" / "English" button.

export const CONTENT = {
  en: {
    langCode: 'en',
    langToggleLabel: 'Español',
    header: {
      portalName: 'CUSTOMER PORTAL',
      help: 'Help',
      needHelpPrefix: 'Need help? Call',
    },
    hero: {
      heading: 'Customer Portal',
      subtitle:
        'Managing your account just got easier. View invoices, make secure payments, and manage your account anytime.',
    },
    signIn: {
      title: 'SIGN IN',
      description: 'View your full account',
      inputLabel: 'Email or mobile number',
      inputPlaceholder: 'you@example.com or (407) 555-0123',
      submit: 'SEND VERIFICATION CODE',
      submitting: 'SENDING…',
      errorEmpty: 'Please enter your email or mobile number.',
      successTitle: 'Check your inbox',
      successBody:
        'If we find a matching account, a verification code will be sent shortly.',
      successAction: 'Use a different contact',
    },
    pay: {
      title: 'PAY AN INVOICE',
      description: 'Make a one-time payment—no sign-in needed',
      inputLabel: 'Invoice number',
      inputPlaceholder: 'e.g. INV-000123',
      submit: 'CONTINUE',
      submitting: 'LOOKING UP…',
      errorEmpty: 'Please enter an invoice number.',
      successTitle: 'Invoice found',
      successBody:
        "We'll take you to a secure payment page for this invoice shortly.",
      successAction: 'Use a different invoice',
    },
    benefits: {
      viewInvoices: {
        title: 'VIEW INVOICES',
        body: 'See your invoice history and account details.',
      },
      makePayments: {
        title: 'MAKE PAYMENTS',
        body: 'Make secure payments quickly and easily.',
      },
      manageAutopay: {
        title: 'MANAGE AUTOPAY',
        body: 'Set up and manage autopay on your schedule.',
      },
    },
    footer: {
      rights: 'ZMEX Disposal, Inc. All rights reserved.',
    },
    verify: {
      heading: 'Verify your identity',
      sentTo: 'We sent a 6-digit code to',
      checkSpamHint: "Don't see it? Check your spam or junk folder.",
      codeLabel: 'Enter the 6-digit verification code',
      submit: 'VERIFY',
      submitting: 'VERIFYING…',
      resendPrompt: "Didn't get a code?",
      resendCta: 'Resend code',
      resendIn: 'Resend available in {seconds}s',
      resending: 'Sending a new code…',
      changeContact: 'Change email or mobile number',
      returnLogin: 'Return to sign-in',
      errorIncomplete: 'Enter all 6 digits.',
      errorGeneric: 'That code did not work. Please try again.',
      errorExpired:
        'This code has expired. Request a new one to continue.',
      errorLocked:
        'Too many incorrect attempts. Request a new code to continue.',
      successRedirect: 'Verified. Taking you to your account…',
    },
    dashboard: {
      welcomeBack: 'Welcome back,',
      overview: 'Account overview',
      lastLogin: 'Last sign-in',
      neverSignedIn: 'First sign-in',
      contactInfo: 'Sign-in method',
      paymentReminderTitle: 'Payment reminder',
      paymentReminderBody:
        'All invoices are due on the 15th of each month. Late fees begin to apply on the 3rd day past due. Account suspension begins on the 10th day past due and varies by account.',
      paymentReminderDismiss: 'Dismiss',
      contactInfoTitle: 'Contact & billing info',
      contactInfoHint:
        'This is how we reach you about invoices and account updates.',
      contactInfoPhone: 'Phone number',
      contactInfoBillingEmail: 'Email for invoices',
      contactInfoMailing: 'Mailing address (for mailed invoices)',
      contactInfoNotProvided: 'Not provided',
      contactInfoEdit: 'Edit',
      contactInfoSave: 'Save changes',
      contactInfoCancel: 'Cancel',
      contactInfoSaving: 'Saving…',
      contactInfoSaved:
        'Saved — we will use these for invoices and account updates.',
      contactInfoAddressLocked:
        'To change your service address, please contact the office at 407-305-2756.',
      confirmTitle: 'Confirm your details',
      confirmBody:
        'We are reaching out to our customers to double-check their information for invoice delivery. Please confirm or update your details below.',
      confirmOneTime:
        "One-time confirmation — once you save, you won't be asked again.",
      confirmMailing: 'Mailing address for invoices',
      confirmInvoicePhone: 'Phone number for invoices',
      confirmInvoiceEmail: 'Email address for invoices',
      confirmPocPhone: 'Point of contact phone number',
      confirmSave: 'Save my info',
      confirmSaving: 'Saving…',
      confirmLater: 'Not now',
      confirmSaved: 'Thanks — your details are confirmed.',
      signOut: 'Sign out',
      signingOut: 'Signing out…',
      selectAccount: 'Select account',
      selectLocation: 'Select service location',
      accountStatus: 'Account status',
      accountStatusValues: {
        active: 'Active',
        past_due: 'Past due',
        on_hold: 'On hold',
      },
      pickAccountTitle: 'Which account would you like to manage?',
      pickAccountBody:
        'You have access to more than one ZMEX account. Choose one to continue.',
      balance: 'Current balance',
      balanceEmpty: 'No balance on record.',
      payNow: 'Pay Now',
      viewInvoices: 'View Invoices',
      getSupport: 'Get Support',
      nextDue: 'Next payment due',
      nextDueEmpty: 'No upcoming payment scheduled.',
      dueOn: 'Due',
      invoicesOpen: 'Open invoices',
      invoicesEmpty: 'No open invoices right now.',
      autopay: 'Autopay',
      autopayOn: 'Enrolled',
      autopayOff: 'Not enrolled',
      manageAutopay: 'Manage Autopay',
      setUpAutopay: 'Set up Autopay',
      billingHeading: 'Billing',
      billingTabInvoices: 'Open invoices',
      billingTabPayments: 'Recent payments',
      invoiceNumber: 'Invoice',
      invoiceDate: 'Date',
      invoiceDueDate: 'Due',
      invoiceAmount: 'Amount',
      invoiceRemaining: 'Balance',
      invoiceStatus: 'Status',
      invoiceStatusValues: {
        open: 'Open',
        past_due: 'Past due',
        paid: 'Paid',
      },
      invoiceActions: 'Actions',
      invoiceView: 'View',
      invoiceDownload: 'Download',
      invoicePay: 'Pay',
      viewAllInvoices: 'View all invoices',
      paymentMethods: 'Manage payment methods',
      manageAutopayLink: 'Manage autopay',
      paymentDate: 'Date',
      paymentMethod: 'Method',
      paymentAmount: 'Amount',
      paymentInvoice: 'Applied to',
      paymentsEmpty: 'No recent payments to show.',
      serviceInfo: 'Service information',
      serviceAddress: 'Service address',
      containerSize: 'Container size',
      collectionFrequency: 'Collection frequency',
      nextCollection: 'Next scheduled collection',
      serviceStatus: 'Service status',
      serviceNotes: 'Notes',
      serviceChangeHint:
        'Need to change your address, container, or schedule? Submit a support request and our team will help.',
      supportHeading: 'Get support',
      supportBody: 'Pick the type of request. We usually respond within one business day.',
      supportCategories: {
        missed_pickup: {
          title: 'Report a missed pickup',
          body: 'Let us know if your container was not serviced on schedule.',
        },
        extra_pickup: {
          title: 'Request an extra pickup',
          body: 'Ask for an additional service between your regular days.',
        },
        container_issue: {
          title: 'Report a container issue',
          body: 'Damage, missing lid, wheels, wrong size, or anything unusual.',
        },
        billing_question: {
          title: 'Ask a billing question',
          body: 'Questions about an invoice, a payment, or your balance.',
        },
        account_service_change: {
          title: 'Request an account or service change',
          body: 'Change of address, container size, frequency, or ownership.',
        },
        other: {
          title: 'Other',
          body: 'Anything else we can help with.',
        },
      },
      placeholderNote:
        'You are viewing placeholder data. Real invoice, autopay, and payment information will appear once Zoho Books sync is enabled.',
    },
    support: {
      newHeading: 'New support request',
      newSub:
        'Tell us what happened and we\u2019ll route it to the right team. Fields marked with * are required.',
      fieldAccount: 'Account *',
      fieldLocation: 'Service location',
      fieldCategory: 'Request category *',
      fieldSubject: 'Subject *',
      fieldSubjectPlaceholder: 'A short summary',
      fieldMessage: 'What happened? *',
      fieldMessagePlaceholder: 'Please share as much detail as you can…',
      fieldPhoto: 'Attach a photo (optional)',
      fieldPhotoHint: 'JPG, PNG, WebP, or HEIC. Up to 8 MB.',
      fieldContact: 'How should we respond?',
      contactMasked: 'Response will be sent to your signed-in contact:',
      contactChoiceText: 'Text',
      contactChoiceEmail: 'Email',
      contactChoicePhone: 'Phone call',
      submit: 'Submit request',
      submitting: 'Submitting…',
      errorGeneric: 'We couldn\u2019t submit your request. Please try again.',
      chooseCategory: 'Choose a category',
      chooseLocation: 'Any location on this account',
      confirmationHeading: 'Request received',
      confirmationSub:
        'Thanks — we\u2019ve got it. A team member will follow up on your preferred channel.',
      confirmationNumberLabel: 'Confirmation number',
      confirmationCategoryLabel: 'Category',
      confirmationLocationLabel: 'Service location',
      confirmationSubmittedLabel: 'Submitted',
      confirmationStatusLabel: 'Status',
      confirmationNextStep: 'Expected next step',
      confirmationNextStepBody:
        'Our team will review your request and respond via your preferred method.',
      backToDashboard: 'Back to dashboard',
      viewMyRequests: 'View my requests',
      listHeading: 'My support requests',
      listEmpty: 'You haven\u2019t submitted any support requests yet.',
      listNew: 'New request',
      statusValues: {
        submitted: 'Submitted',
        reviewing: 'Reviewing',
        scheduled: 'Scheduled',
        resolved: 'Resolved',
        closed: 'Closed',
      },
      categoryLabels: {
        missed_pickup: 'Missed pickup',
        extra_pickup: 'Extra pickup',
        container_issue: 'Container issue',
        billing_question: 'Billing question',
        account_service_change: 'Account or service change',
        other: 'Other',
      },
      detailHeading: 'Support request',
      detailFollowUpHeading: 'Add a follow-up',
      detailFollowUpSub:
        'You can add extra details or a photo while this request is still open.',
      detailFollowUpPlaceholder: 'Add more information…',
      detailFollowUpSubmit: 'Send update',
      detailFollowUpSubmitting: 'Sending…',
      detailFollowUpClosed:
        'This request is closed. If you need more help, please open a new request.',
      detailAttachments: 'Attachments',
      detailUpdates: 'Updates',
      detailNoUpdates: 'No updates yet.',
      viewDetails: 'View details',
      backToRequests: 'Back to my requests',
    },
  },
  es: {
    langCode: 'es',
    langToggleLabel: 'English',
    header: {
      portalName: 'PORTAL DE CLIENTES',
      help: 'Ayuda',
      needHelpPrefix: '¿Necesita ayuda? Llame al',
    },
    hero: {
      heading: 'Portal de Clientes',
      subtitle:
        'Administrar su cuenta es más fácil que nunca. Consulte facturas, realice pagos seguros y administre su cuenta en cualquier momento.',
    },
    signIn: {
      title: 'INICIAR SESIÓN',
      description: 'Vea toda su cuenta',
      inputLabel: 'Correo electrónico o número de móvil',
      inputPlaceholder: 'usted@ejemplo.com o (407) 555-0123',
      submit: 'ENVIAR CÓDIGO DE VERIFICACIÓN',
      submitting: 'ENVIANDO…',
      errorEmpty: 'Ingrese su correo electrónico o número de móvil.',
      successTitle: 'Revise su bandeja de entrada',
      successBody:
        'Si encontramos una cuenta que coincida, se enviará un código de verificación en breve.',
      successAction: 'Usar otro contacto',
    },
    pay: {
      title: 'PAGAR UNA FACTURA',
      description: 'Realice un pago único, sin iniciar sesión',
      inputLabel: 'Número de factura',
      inputPlaceholder: 'ej. INV-000123',
      submit: 'CONTINUAR',
      submitting: 'BUSCANDO…',
      errorEmpty: 'Ingrese un número de factura.',
      successTitle: 'Factura encontrada',
      successBody:
        'En breve lo llevaremos a una página de pago segura para esta factura.',
      successAction: 'Usar otra factura',
    },
    benefits: {
      viewInvoices: {
        title: 'VER FACTURAS',
        body: 'Consulte su historial de facturas y los detalles de su cuenta.',
      },
      makePayments: {
        title: 'REALIZAR PAGOS',
        body: 'Realice pagos seguros de forma rápida y sencilla.',
      },
      manageAutopay: {
        title: 'ADMINISTRAR PAGO AUTOMÁTICO',
        body: 'Configure y administre el pago automático en su horario.',
      },
    },
    footer: {
      rights: 'ZMEX Disposal, Inc. Todos los derechos reservados.',
    },
    verify: {
      heading: 'Verifique su identidad',
      sentTo: 'Enviamos un código de 6 dígitos a',
      checkSpamHint: '¿No lo ve? Revise su carpeta de spam o correo no deseado.',
      codeLabel: 'Ingrese el código de verificación de 6 dígitos',
      submit: 'VERIFICAR',
      submitting: 'VERIFICANDO…',
      resendPrompt: '¿No recibió el código?',
      resendCta: 'Reenviar código',
      resendIn: 'Reenvío disponible en {seconds}s',
      resending: 'Enviando un código nuevo…',
      changeContact: 'Cambiar correo electrónico o número de móvil',
      returnLogin: 'Volver al inicio de sesión',
      errorIncomplete: 'Ingrese los 6 dígitos.',
      errorGeneric: 'Ese código no funcionó. Inténtelo de nuevo.',
      errorExpired:
        'Este código expiró. Solicite uno nuevo para continuar.',
      errorLocked:
        'Demasiados intentos incorrectos. Solicite un nuevo código para continuar.',
      successRedirect: 'Verificado. Llevándolo a su cuenta…',
    },
    dashboard: {
      welcomeBack: 'Bienvenido de nuevo,',
      overview: 'Resumen de la cuenta',
      lastLogin: 'Último inicio de sesión',
      neverSignedIn: 'Primer inicio de sesión',
      contactInfo: 'Método de inicio de sesión',
      paymentReminderTitle: 'Recordatorio de pago',
      paymentReminderBody:
        'Todas las facturas vencen el día 15 de cada mes. Los cargos por pago atrasado comienzan a aplicarse el 3er día de atraso. La suspensión de la cuenta comienza el 10mo día de atraso y varía según la cuenta.',
      paymentReminderDismiss: 'Descartar',
      contactInfoTitle: 'Información de contacto y facturación',
      contactInfoHint:
        'Así nos comunicaremos con usted sobre facturas y avisos de la cuenta.',
      contactInfoPhone: 'Número de teléfono',
      contactInfoBillingEmail: 'Correo electrónico para facturas',
      contactInfoMailing: 'Dirección postal (para facturas por correo)',
      contactInfoNotProvided: 'No proporcionado',
      contactInfoEdit: 'Editar',
      contactInfoSave: 'Guardar cambios',
      contactInfoCancel: 'Cancelar',
      contactInfoSaving: 'Guardando…',
      contactInfoSaved:
        'Guardado — usaremos estos datos para facturas y avisos de la cuenta.',
      contactInfoAddressLocked:
        'Para cambiar su dirección de servicio, llame a la oficina al 407-305-2756.',
      confirmTitle: 'Confirme sus datos',
      confirmBody:
        'Estamos contactando a nuestros clientes para verificar su información para la entrega de facturas. Confirme o actualice sus datos a continuación.',
      confirmOneTime:
        'Confirmación única — una vez guardada, no se le volverá a pedir.',
      confirmMailing: 'Dirección postal para facturas',
      confirmInvoicePhone: 'Número de teléfono para facturas',
      confirmInvoiceEmail: 'Correo electrónico para facturas',
      confirmPocPhone: 'Teléfono del punto de contacto',
      confirmSave: 'Guardar mi información',
      confirmSaving: 'Guardando…',
      confirmLater: 'Ahora no',
      confirmSaved: 'Gracias — sus datos están confirmados.',
      signOut: 'Cerrar sesión',
      signingOut: 'Cerrando sesión…',
      selectAccount: 'Seleccionar cuenta',
      selectLocation: 'Seleccionar ubicación de servicio',
      accountStatus: 'Estado de la cuenta',
      accountStatusValues: {
        active: 'Activa',
        past_due: 'Vencida',
        on_hold: 'En espera',
      },
      pickAccountTitle: '¿Qué cuenta desea administrar?',
      pickAccountBody:
        'Tiene acceso a más de una cuenta ZMEX. Elija una para continuar.',
      balance: 'Saldo actual',
      balanceEmpty: 'Sin saldo registrado.',
      payNow: 'Pagar ahora',
      viewInvoices: 'Ver facturas',
      getSupport: 'Obtener ayuda',
      nextDue: 'Próximo pago',
      nextDueEmpty: 'No hay pagos próximos programados.',
      dueOn: 'Vence',
      invoicesOpen: 'Facturas abiertas',
      invoicesEmpty: 'No hay facturas abiertas.',
      autopay: 'Pago automático',
      autopayOn: 'Inscrito',
      autopayOff: 'No inscrito',
      manageAutopay: 'Administrar pago automático',
      setUpAutopay: 'Configurar pago automático',
      billingHeading: 'Facturación',
      billingTabInvoices: 'Facturas abiertas',
      billingTabPayments: 'Pagos recientes',
      invoiceNumber: 'Factura',
      invoiceDate: 'Fecha',
      invoiceDueDate: 'Vence',
      invoiceAmount: 'Monto',
      invoiceRemaining: 'Saldo',
      invoiceStatus: 'Estado',
      invoiceStatusValues: {
        open: 'Abierta',
        past_due: 'Vencida',
        paid: 'Pagada',
      },
      invoiceActions: 'Acciones',
      invoiceView: 'Ver',
      invoiceDownload: 'Descargar',
      invoicePay: 'Pagar',
      viewAllInvoices: 'Ver todas las facturas',
      paymentMethods: 'Administrar métodos de pago',
      manageAutopayLink: 'Administrar pago automático',
      paymentDate: 'Fecha',
      paymentMethod: 'Método',
      paymentAmount: 'Monto',
      paymentInvoice: 'Aplicado a',
      paymentsEmpty: 'No hay pagos recientes.',
      serviceInfo: 'Información del servicio',
      serviceAddress: 'Dirección del servicio',
      containerSize: 'Tamaño del contenedor',
      collectionFrequency: 'Frecuencia de recolección',
      nextCollection: 'Próxima recolección',
      serviceStatus: 'Estado del servicio',
      serviceNotes: 'Notas',
      serviceChangeHint:
        '¿Necesita cambiar dirección, contenedor u horario? Envíe una solicitud de ayuda y nuestro equipo lo atenderá.',
      supportHeading: 'Obtener ayuda',
      supportBody:
        'Elija el tipo de solicitud. Normalmente respondemos en un día hábil.',
      supportCategories: {
        missed_pickup: {
          title: 'Reportar recolección omitida',
          body: 'Indique si su contenedor no fue atendido a tiempo.',
        },
        extra_pickup: {
          title: 'Solicitar recolección adicional',
          body: 'Pida un servicio extra entre sus días habituales.',
        },
        container_issue: {
          title: 'Reportar un problema con el contenedor',
          body: 'Daños, tapa faltante, ruedas, tamaño incorrecto, etc.',
        },
        billing_question: {
          title: 'Consulta de facturación',
          body: 'Preguntas sobre una factura, un pago o su saldo.',
        },
        account_service_change: {
          title: 'Solicitar cambio de cuenta o servicio',
          body: 'Cambio de dirección, tamaño, frecuencia o propiedad.',
        },
        other: {
          title: 'Otro',
          body: 'Cualquier otra cosa en la que podamos ayudar.',
        },
      },
      placeholderNote:
        'Está viendo datos de marcador de posición. La información real de facturación aparecerá cuando se active la sincronización con Zoho Books.',
    },
    support: {
      newHeading: 'Nueva solicitud de ayuda',
      newSub:
        'Cuéntenos lo que sucedió y lo dirigiremos al equipo correcto. Los campos con * son obligatorios.',
      fieldAccount: 'Cuenta *',
      fieldLocation: 'Ubicación de servicio',
      fieldCategory: 'Categoría de la solicitud *',
      fieldSubject: 'Asunto *',
      fieldSubjectPlaceholder: 'Un breve resumen',
      fieldMessage: '¿Qué sucedió? *',
      fieldMessagePlaceholder: 'Comparta todo el detalle posible…',
      fieldPhoto: 'Adjuntar una foto (opcional)',
      fieldPhotoHint: 'JPG, PNG, WebP o HEIC. Hasta 8 MB.',
      fieldContact: '¿Cómo debemos responder?',
      contactMasked: 'La respuesta se enviará a su contacto actual:',
      contactChoiceText: 'Texto',
      contactChoiceEmail: 'Correo electrónico',
      contactChoicePhone: 'Llamada telefónica',
      submit: 'Enviar solicitud',
      submitting: 'Enviando…',
      errorGeneric:
        'No pudimos enviar su solicitud. Inténtelo de nuevo.',
      chooseCategory: 'Seleccione una categoría',
      chooseLocation: 'Cualquier ubicación de esta cuenta',
      confirmationHeading: 'Solicitud recibida',
      confirmationSub:
        'Gracias — la recibimos. Un miembro del equipo le contactará por su canal preferido.',
      confirmationNumberLabel: 'Número de confirmación',
      confirmationCategoryLabel: 'Categoría',
      confirmationLocationLabel: 'Ubicación de servicio',
      confirmationSubmittedLabel: 'Enviado',
      confirmationStatusLabel: 'Estado',
      confirmationNextStep: 'Próximo paso esperado',
      confirmationNextStepBody:
        'Nuestro equipo revisará su solicitud y responderá por su método preferido.',
      backToDashboard: 'Volver al panel',
      viewMyRequests: 'Ver mis solicitudes',
      listHeading: 'Mis solicitudes de ayuda',
      listEmpty: 'Aún no ha enviado solicitudes de ayuda.',
      listNew: 'Nueva solicitud',
      statusValues: {
        submitted: 'Enviada',
        reviewing: 'En revisión',
        scheduled: 'Programada',
        resolved: 'Resuelta',
        closed: 'Cerrada',
      },
      categoryLabels: {
        missed_pickup: 'Recolección omitida',
        extra_pickup: 'Recolección adicional',
        container_issue: 'Problema con el contenedor',
        billing_question: 'Consulta de facturación',
        account_service_change: 'Cambio de cuenta o servicio',
        other: 'Otro',
      },
      detailHeading: 'Solicitud de ayuda',
      detailFollowUpHeading: 'Agregar seguimiento',
      detailFollowUpSub:
        'Puede agregar más detalles o una foto mientras la solicitud siga abierta.',
      detailFollowUpPlaceholder: 'Agregar más información…',
      detailFollowUpSubmit: 'Enviar actualización',
      detailFollowUpSubmitting: 'Enviando…',
      detailFollowUpClosed:
        'Esta solicitud está cerrada. Si necesita más ayuda, abra una nueva solicitud.',
      detailAttachments: 'Archivos adjuntos',
      detailUpdates: 'Actualizaciones',
      detailNoUpdates: 'Aún no hay actualizaciones.',
      viewDetails: 'Ver detalles',
      backToRequests: 'Volver a mis solicitudes',
    },
  },
};

export const PHONE_NUMBER = '407-305-2756';
export const PHONE_TEL = '+14073052756';
